import { datadogRum } from '@datadog/browser-rum'
import { ApolloError } from '@apollo/client'
import {
  ISuggestedProductsResponse,
  ISuggestedProductsVariables,
} from '../../graphQL/queries/suggestedItems/types'
import {
  setStatus,
  setSuggestedProducts,
} from '../../redux/slices/suggestedProducts'
import { generateDataDogContext } from '../../utils/dataDog'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { useLazyGetSuggestedItems } from '../../graphQL/queries/suggestedItems'
import {
  getFlattedCartItems,
  setProductsQuantity,
} from '../../utils/utilsForCatalogReducer'
import { castIProducts } from '../../utils/castICatalogItems'

export const useSuggestionItems = () => {
  const dispatch = useAppDispatch()
  const config = useAppSelector((state) => state.defaultSlice.config)
  const showSuggestedItems = useAppSelector(
    (state) => state.defaultSlice.showSuggestedItems,
  )
  const { items, groups, activePromotions } = useAppSelector(
    (state) => state.cartSlice,
  )
  const keepErrorStatus =
    config?.globals?.recommendedProducts?.keepSuggestedItemsErrorStatus === true
  const store = useAppStore()

  /**
   * Apollo calls this from a promise whose rejection it only logs in
   * development, so a throw here would otherwise vanish in production with
   * the list left on its previous items. `OK` is set only once the new items
   * are in.
   */
  const onCompleted = ({ suggestedItems }: ISuggestedProductsResponse) => {
    try {
      const { flattedCartItems } = getFlattedCartItems({
        items,
        groups,
      })
      const products = castIProducts(suggestedItems, config, false)
      const result = setProductsQuantity({
        products,
        flattedCartItems,
        config,
        activePromotions,
        isSuggestedProduct: true,
        isSplitPromotionTagAllowed:
          config?.options?.splitPromotionTagRecommendedProducts,
      })
      dispatch(setSuggestedProducts(result))
      dispatch(setStatus('OK'))
    } catch (exception) {
      if (keepErrorStatus) dispatch(setStatus('ERROR'))
      const context = generateDataDogContext({
        title: 'Suggested items could not be merged with the cart',
        extraInfo: { function: 'useSuggestionItems > onCompleted' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(exception, context)
    }
  }

  const onError = (error: ApolloError) => {
    const context = generateDataDogContext({
      title: 'Get suggested items data got an error',
      extraInfo: { function: 'useSuggestionItemsData > onError' },
    })
    dispatch(setStatus('ERROR'))
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }
  const [getSuggestedItems] = useLazyGetSuggestedItems({
    onCompleted,
    onError,
  })

  const fetchSuggestedItems = async ({
    storefrontName,
    sessionUid,
    limit,
  }: ISuggestedProductsVariables) => {
    try {
      // TODO: adding this validation cause the config cannot convert strings to numbers
      if (showSuggestedItems) {
        dispatch(setStatus('LOADING'))
        const parsedLimit = +limit
        await getSuggestedItems({
          variables: {
            storefrontName,
            sessionUid,
            limit: parsedLimit || 1000,
          },
        })
      }
    } catch (exception) {
      // Defensive: Apollo never rejects the lazy query's promise (a failed
      // query resolves and goes to `onError`), so this catches only a
      // synchronous throw from the execute call itself.
      if (keepErrorStatus) dispatch(setStatus('ERROR'))
      const context = generateDataDogContext({
        title: 'Get suggested items data got an error',
        extraInfo: { function: 'getSuggestedItems' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(exception, context)
    } finally {
      // `onCompleted` and `onError` have already run by the time the `await`
      // above resumes, so an unconditional `OK` here overwrites the `ERROR`
      // either of them set. With the flag on it only settles a fetch this
      // call started that is still on `LOADING`, and a call that fetched
      // nothing leaves the status as it was. The `LOADING` branch is reached
      // when the container unmounts mid-fetch: Apollo's callbacks stop with
      // the component, so neither runs and a failure is neither reported nor
      // kept, while the query still resolves here.
      const unsettled =
        showSuggestedItems &&
        store.getState().suggestedProductsSlice.status === 'LOADING'
      if (!keepErrorStatus || unsettled) dispatch(setStatus('OK'))
    }
  }

  return fetchSuggestedItems
}

export default useSuggestionItems
